import React from 'react';
import {
  List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, Chip, Box, Tooltip, Typography,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { Policy } from '../../store/api/githubApi';

export function describePolicyRule(p: Pick<Policy, 'rule_kind' | 'category' | 'params'>): string {
  if (p.rule_kind === 'max_severity_count') {
    return `More than ${p.params.max_count} ${p.category} issue(s) at ${p.params.min_severity}+ severity`;
  }
  if (p.rule_kind === 'max_age_days') {
    return `${p.category} issue at ${p.params.min_severity}+ severity open past ${p.params.max_days} day(s)`;
  }
  if (p.rule_kind === 'min_category_score') {
    return `${p.category} score below ${p.params.min_score}`;
  }
  return `${p.category} — ${p.rule_kind}`;
}

const TIER_COLOR: Record<Policy['tier'], 'default' | 'info' | 'primary'> = {
  global: 'default',
  account: 'info',
  repo: 'primary',
};

interface PolicyListProps {
  policies: Policy[];
  editableTier: Policy['tier']; // rows at this tier get edit/delete controls on this page
  onEdit: (policy: Policy) => void;
  onDelete: (policy: Policy) => void;
  emptyMessage: string;
}

export const PolicyList: React.FC<PolicyListProps> = ({
  policies, editableTier, onEdit, onDelete, emptyMessage,
}) => {
  if (policies.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <List disablePadding>
      {policies.map((policy, i) => {
        const editable = policy.tier === editableTier;
        return (
          <ListItem key={policy.id} divider={i < policies.length - 1} sx={{ py: 1.5 }}>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={policy.tier} size="small" color={TIER_COLOR[policy.tier]} sx={{ textTransform: 'capitalize', height: 20, fontSize: 11 }} />
                  <Chip
                    label={policy.action}
                    size="small"
                    color={policy.action === 'block' ? 'error' : 'warning'}
                    variant="outlined"
                    sx={{ textTransform: 'capitalize', height: 20, fontSize: 11 }}
                  />
                  {!policy.enabled && (
                    <Chip label="Disabled" size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                  )}
                </Box>
              }
              secondary={
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {describePolicyRule(policy)}
                </Typography>
              }
            />
            {editable && (
              <ListItemSecondaryAction>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => onEdit(policy)}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => onDelete(policy)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </ListItemSecondaryAction>
            )}
          </ListItem>
        );
      })}
    </List>
  );
};
